import React from 'react';
import { Box, Typography } from '@mui/material';
import { ZoneNumeral } from './Badges';
import { ZoneCounts, fixtureTiles, floorFixtures, indexedThumbTarget, walkFixtureIds, zoneCounts, zoneIsClear } from './format';
import { C, FLOOR_SLOTS, FLOOR_TEMPLATE, MONO, NARROW_ZONES, SHADOW, VERB } from './tokens';
import { MoveKind, RestockV2Fixture, RestockV2Move, RestockV2Plan, RestockV2ThumbTarget } from './types';

const PILL_ORDER: MoveKind[] = ['display', 'from_office', 'bring_back', 'on_arrival'];

// 上 3 · 拿 1 · 回 7 · 等 13 — pieces, one colour per verb, the same colours as the cards.
const ZonePills: React.FC<{ counts: ZoneCounts; big: boolean }> = ({ counts, big }) => {
  const shown = PILL_ORDER
    .map((kind) => ({ kind, qty: (kind === 'on_arrival' ? counts.waiting : counts[kind]) || 0 }))
    .filter((row) => row.qty > 0);
  const size = big ? 12 : 10;
  if (!shown.length) {
    return (
      <Box sx={{ display: 'flex', gap: '3px', mt: 'auto' }}>
        <Box
          component="span"
          sx={{ fontSize: size, fontWeight: 800, px: '6px', borderRadius: '99px', bgcolor: C.paper2, color: C.ink3 }}
        >
          不用動
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mt: 'auto' }}>
      {shown.map((row) => (
        <Box
          key={row.kind}
          component="span"
          sx={{
            fontSize: size, fontWeight: 800, px: '6px', borderRadius: '99px', whiteSpace: 'nowrap',
            bgcolor: VERB[row.kind].wash, color: VERB[row.kind].colour,
          }}
        >
          {VERB[row.kind].mark}
          <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, ml: '2px' }}>{row.qty}</Box>
        </Box>
      ))}
    </Box>
  );
};

// 現在 vs 放得下: the filled bar is what is on the rail, the dotted mark is the capacity.
const MiniCapacity: React.FC<{ now: number; capacity: number; height: number }> = ({ now, capacity, height }) => {
  const scale = Math.max(now, capacity, 1);
  const over = now > capacity;
  return (
    <Box sx={{ height, bgcolor: C.paper2, borderRadius: '99px', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${Math.round((now / scale) * 100)}%`, bgcolor: over ? C.back : C.ink }} />
      <Box
        component="span" aria-hidden="true"
        sx={{
          position: 'absolute', top: -2, bottom: -2, left: `${Math.round((capacity / scale) * 100)}%`,
          borderLeft: '2px dotted', borderColor: C.ink2,
        }}
      />
    </Box>
  );
};

const Zone: React.FC<{
  fixture: RestockV2Fixture; index: number; plan: RestockV2Plan; moves: RestockV2Move[];
  thumbIndex: Record<string, RestockV2ThumbTarget>; desktop: boolean; selected: boolean;
  onZone: (id: string) => void;
}> = ({ fixture, index, plan, moves, thumbIndex, desktop, selected, onZone }) => {
  const counts = zoneCounts(moves, fixture.id);
  const clear = zoneIsClear(counts);
  const slot = FLOOR_SLOTS[fixture.id];
  const tiles = fixtureTiles(fixture, plan);
  const limit = desktop ? 10 : (NARROW_ZONES.indexOf(fixture.id) >= 0 ? 3 : 6);
  const strip = tiles.slice(0, limit);
  const rest = tiles.length - strip.length;
  const thumb = desktop ? 56 : 26;
  return (
    <Box
      component="button" type="button" onClick={() => onZone(fixture.id)}
      aria-label={`${fixture.label} · 現在 ${fixture.now_pieces} 件 · 容量 ${fixture.capacity} 件`}
      aria-pressed={desktop ? selected : undefined}
      sx={{
        appearance: 'none', width: '100%', textAlign: 'left', cursor: 'pointer',
        gridArea: slot?.area ?? 'auto / 1 / auto / -1',
        // sized to its content: a tall side rail must not be stretched to the height of two
        // pants rails, which is what left the big empty area in the first desktop pass
        alignSelf: 'start',
        bgcolor: selected ? C.card : (clear ? C.wash : C.card),
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? C.back : C.line,
        borderRadius: '10px', p: desktop ? 1.25 : '7px 7px 8px',
        boxShadow: selected ? '0 2px 12px rgba(191,66,24,.22)' : SHADOW,
        display: 'flex', flexDirection: 'column', gap: desktop ? 0.9 : '5px',
        minHeight: desktop ? 0 : 94,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
        <ZoneNumeral index={index} size={desktop ? 12 : 10} bg={selected ? C.back : C.ink} />
        <Typography
          component="span"
          sx={{ fontSize: desktop ? 14 : 12, fontWeight: 800, lineHeight: 1.25, letterSpacing: '-.01em' }}
        >
          {fixture.label}
        </Typography>
      </Box>
      <Box
        aria-hidden="true"
        sx={{ height: 0, borderTop: slot?.shelf ? '5px double' : '2px solid', borderColor: C.line2 }}
      />
      <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
        {strip.map((tile, position) => {
          const target = indexedThumbTarget(thumbIndex, tile);
          return target.image_url ? (
            <Box
              key={`${tile.sku}:${tile.color ?? ''}:${tile.size ?? ''}:${position}`}
              component="img" src={target.image_url} alt="" loading="lazy" data-testid="rack-thumb"
              sx={{
                width: thumb, height: Math.round((thumb * 4) / 3), objectFit: 'cover', borderRadius: '3px',
                bgcolor: C.paper2, border: '1px solid', borderColor: C.line, display: 'block', flex: 'none',
              }}
            />
          ) : (
            <Box
              key={`${tile.sku}:${tile.color ?? ''}:${tile.size ?? ''}:${position}`}
              sx={{
                width: thumb, height: Math.round((thumb * 4) / 3), borderRadius: '3px', bgcolor: C.paper2,
                border: '1px solid', borderColor: C.line, flex: 'none',
              }}
            />
          );
        })}
        {rest > 0 && (
          <Typography
            component="span"
            sx={{ fontFamily: MONO, fontSize: desktop ? 12 : 10, color: C.ink3, fontWeight: 600 }}
          >
            +{rest}
          </Typography>
        )}
      </Box>
      <MiniCapacity now={fixture.now_pieces} capacity={fixture.capacity} height={desktop ? 6 : 4} />
      <ZonePills counts={counts} big={desktop} />
    </Box>
  );
};

interface FloorPlanProps {
  plan: RestockV2Plan;
  moves: RestockV2Move[];
  thumbIndex: Record<string, RestockV2ThumbTarget>;
  desktop: boolean;
  selectedId: string | null;
  onZone: (fixtureId: string) => void;
}

// The counter drawn as it is walked: the door at the bottom, the six fixtures in their real places.
const FloorPlan: React.FC<FloorPlanProps> = ({ plan, moves, thumbIndex, desktop, selectedId, onZone }) => {
  const fixtures = floorFixtures(plan.racks);
  const walk = walkFixtureIds(plan);
  return (
    <Box sx={{ bgcolor: C.paper2, border: '1px solid', borderColor: C.line2, borderRadius: '14px', p: 1.25 }}>
      <Typography
        sx={{ fontSize: 12, letterSpacing: '.14em', fontWeight: 800, color: C.ink3, textAlign: 'center', pb: 1 }}
      >
        櫃位平面圖 · 點一個架子看它該長怎樣
      </Typography>
      <Box
        sx={{
          display: 'grid', gridTemplateColumns: '1.06fr .92fr 1.06fr', gridTemplateAreas: FLOOR_TEMPLATE,
          gap: desktop ? 1 : '7px', alignItems: 'start',
        }}
      >
        {fixtures.map((fixture) => (
          <Zone
            key={fixture.id} fixture={fixture} index={walk.indexOf(fixture.id)} plan={plan} moves={moves}
            thumbIndex={thumbIndex} desktop={desktop} selected={selectedId === fixture.id} onZone={onZone}
          />
        ))}
      </Box>
      <Typography
        sx={{
          textAlign: 'center', fontSize: 12, letterSpacing: '.18em', color: C.ink3, fontWeight: 700, pt: 1,
          '&::before': {
            content: '""', display: 'block', height: 0, borderTop: '2px dashed',
            borderColor: C.line2, mx: '22%', mb: '6px',
          },
        }}
      >
        店 門 口
      </Typography>
    </Box>
  );
};

export default FloorPlan;
